export const noDomain = (host: string) =>
  ['localhost', '127.0.0.1'].some((path) => host.includes(path))

export const excluded = (path?: string) =>
  !path || /(_next|\.(js|css|jpg|jpeg|png|gif|ico|svg|json))/.test(path)

export const toJson = (data: string | Uint8Array) => JSON.parse(Buffer.from(data).toString('utf8'))

// export const shopDNSTemplate = (domain: string) => `domains/${domain}.json`

// export const shopSettingTemplate = (shopId: string) => `shops/${shopId}/setting.json`

// export const productsTemplate = (shopId: string) => `shops/${shopId}/products/`

// export const designsTemplate = (shopId: string) => `shops/${shopId}/designs/`
// export const previewsTemplate = (shopId: string) => `shops/${shopId}/previews/`

// export const reviewsTemplate = (shopId: string, productId: string) =>
//   `shops/${shopId}/reviews/${productId}.json`

export const deepCloneObject = (inObject: Record<string, any>): any => {
  if (typeof inObject !== 'object' || inObject === null) {
    return inObject // Return the value if inObject is not an object
  }

  // Create an array or object to hold the values
  const outObject = Array.isArray(inObject) ? [] : {}

  for (const key in inObject) {
    // @ts-ignore
    const value = inObject[key]

    // Recursively (deep) copy for nested objects, including arrays
    // @ts-ignore
    outObject[key] = deepCloneObject(value)
  }

  return outObject
}

export const parseCookie = (str: string) =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce((acc: { [key: string]: string }, v: string[]) => {
      acc[decodeURIComponent(v[0]?.trim())] = decodeURIComponent(v[1]?.trim())
      return acc
    }, {})

export interface DifferenceCreate {
  type: 'CREATE'
  path: (string | number)[]
  value: any
}

export interface DifferenceRemove {
  type: 'REMOVE'
  path: (string | number)[]
  oldValue: any
}

export interface DifferenceChange {
  type: 'CHANGE'
  path: (string | number)[]
  value: any
  oldValue: any
}

export type Difference = DifferenceCreate | DifferenceRemove | DifferenceChange

interface Options {
  cyclesFix: boolean
}

const richTypes = { Date: true, RegExp: true, String: true, Number: true }

export default function diff(
  obj: Record<string, any> | any[],
  newObj: Record<string, any> | any[],
  options: Partial<Options> = { cyclesFix: true },
  _stack: Record<string, any>[] = []
): Difference[] {
  const diffs: Difference[] = []
  const isObjArray = Array.isArray(obj)
  const isNewObjArray = Array.isArray(newObj)

  for (const key in obj) {
    const objKey = isObjArray ? obj[+key] : obj[key]
    const path = isObjArray ? +key : key
    if (!(key in newObj)) {
      diffs.push({
        type: 'REMOVE',
        path: [path],
        oldValue: isObjArray ? obj[+key] : obj[key]
      })
      continue
    }
    const newObjKey = isNewObjArray ? newObj[+key] : newObj[key]
    const areCompatibleObjects =
      typeof objKey === 'object' &&
      typeof newObjKey === 'object' &&
      Array.isArray(objKey) === Array.isArray(newObjKey)
    const objKeyType = Object.getPrototypeOf(objKey)?.constructor?.name as keyof typeof richTypes
    if (
      objKey &&
      newObjKey &&
      areCompatibleObjects &&
      !richTypes[objKeyType] &&
      (!options.cyclesFix || !_stack.includes(objKey))
    ) {
      diffs.push(
        ...diffs,
        ...diff(objKey, newObjKey, options, options.cyclesFix ? _stack.concat([objKey]) : []).map(
          (difference) => {
            difference.path.unshift(path)
            return difference
          }
        )
      )
    } else if (
      objKey !== newObjKey &&
      // treat NaN values as equivalent
      !(Number.isNaN(objKey) && Number.isNaN(newObjKey)) &&
      !(
        areCompatibleObjects &&
        (isNaN(objKey) ? objKey + '' === newObjKey + '' : +objKey === +newObjKey)
      )
    ) {
      diffs.push({
        path: [path],
        type: 'CHANGE',
        value: newObjKey,
        oldValue: objKey
      })
    }
  }

  for (const key in newObj) {
    if (!(key in obj)) {
      diffs.push({
        type: 'CREATE',
        path: [isNewObjArray ? +key : key],
        value: isNewObjArray ? newObj[+key] : newObj[key]
      })
    }
  }
  return diffs
}

export const printDiff = (diffs: Difference[]): string[] => {
  return diffs.map((diff) => {
    if (diff.type === 'CREATE') {
      return `Created ${diff.path.join('.')} with value: ${JSON.stringify(diff.value)}`
    }
    if (diff.type === 'REMOVE') {
      return `Removed ${diff.path.join('.')}`
    }
    return `Updated ${diff.path.join('.')}\n From: ${JSON.stringify(
      diff.oldValue
    )}\n To: ${JSON.stringify(diff.value)}`
  })
}
